import {Button} from '@dropbox/dig-components/buttons';
import {Toggle} from '@dropbox/dig-components/controls';
import {FormLabel, FormRow} from '@dropbox/dig-components/form_row';
import {Modal} from '@dropbox/dig-components/modal';
import {SystemBanner} from '@dropbox/dig-components/system_banner';
import {Select} from '@dropbox/dig-components/text_fields';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import {useMutation} from '@tanstack/react-query';
import {pulseUserAtom} from 'atoms/auth';
import {OpenAPI} from 'client/core/OpenAPI';
import {request as __request} from 'client/core/request';
import {CancelButton} from 'components/DSYS/CancelButton';
import {isProd} from 'helpers/environment';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {useState} from 'react';
import {queryClient} from 'views/QueryClientWrapper';

type ReadinessAudience = 'myself' | 'admins';

type ReadinessSendRequest = {
  audience: ReadinessAudience;
  sendEmail: boolean;
  sendSlack: boolean;
};

type ReadinessSendResponse = {
  audience: ReadinessAudience;
  candidates_checked: number;
  actionable_recipients: number;
  anchors_created: number;
  anchors_reused: number;
  slack_scheduled: number;
  email_scheduled: number;
  main_gate_skips: number;
  delivery_gate_skips: number;
  dormant_skips: number;
};

const sendReadinessAttentionNotification = ({
  audience,
  sendEmail,
  sendSlack,
}: ReadinessSendRequest) =>
  __request(OpenAPI, {
    method: 'POST',
    url: '/api/v1/admin/notifications/workstream-readiness/send',
    body: {
      audience,
      send_email: sendEmail,
      send_slack: sendSlack,
    },
    errors: {
      400: 'A delivery channel is required',
      403: 'This tool is unavailable in production',
      404: 'Current employee not found',
      422: 'Validation error',
    },
  }) as Promise<ReadinessSendResponse>;

const getErrorMessage = (error: unknown) =>
  error instanceof Error ? error.message : t('readiness_attention_send_failed');

export const ReadinessAttentionNotificationModal = ({onClose}: {onClose: () => void}) => {
  const user = useAtomValue(pulseUserAtom);
  const [audience, setAudience] = useState<ReadinessAudience>('myself');
  const [sendEmail, setSendEmail] = useState(true);
  const [sendSlack, setSendSlack] = useState(true);
  const [result, setResult] = useState<ReadinessSendResponse | null>(null);

  const mutation = useMutation({
    mutationFn: sendReadinessAttentionNotification,
    onSuccess: async (response) => {
      setResult(response);
      await queryClient.invalidateQueries({queryKey: ['notifications']});
    },
  });

  const canSend = !isProd && (sendEmail || sendSlack) && !mutation.isPending;

  return (
    <Modal isCentered open withCloseButton="Close" onRequestClose={onClose}>
      <Modal.Header hasBottomSpacing="title-standard">
        <Modal.Title weightVariant="emphasized">{t('readiness_attention_send_title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Stack gap="16">
          <SystemBanner tone="alert">
            <Text>{t('readiness_attention_staging_only')}</Text>
          </SystemBanner>

          <Text color="subtle">{t('readiness_attention_send_description')}</Text>

          {mutation.error ? (
            <SystemBanner tone="alert">
              <Text>{getErrorMessage(mutation.error)}</Text>
            </SystemBanner>
          ) : null}

          <FormRow>
            <FormLabel htmlFor="readiness-audience">{t('audience')}</FormLabel>
            <Select
              id="readiness-audience"
              value={audience}
              onChange={(value) => setAudience(value as ReadinessAudience)}
              disabled={mutation.isPending}
            >
              <Select.Option value="myself">
                {`${t('notification_schedule_Myself')} (${user?.ldap ?? ''})`}
              </Select.Option>
              <Select.Option value="admins">{t('notification_schedule_Admins')}</Select.Option>
            </Select>
          </FormRow>

          <FormRow>
            <FormLabel htmlFor="readiness-send-email">{t('send_email')}</FormLabel>
            <Toggle
              id="readiness-send-email"
              checked={sendEmail}
              onChange={() => setSendEmail((enabled) => !enabled)}
              disabled={mutation.isPending}
            />
          </FormRow>

          <FormRow>
            <FormLabel htmlFor="readiness-send-slack">{t('send_slack')}</FormLabel>
            <Toggle
              id="readiness-send-slack"
              checked={sendSlack}
              onChange={() => setSendSlack((enabled) => !enabled)}
              disabled={mutation.isPending}
            />
          </FormRow>

          {result ? (
            <Box
              borderRadius="Large"
              borderWidth="1"
              borderStyle="Solid"
              borderColor="Border Subtle"
              padding="16"
              backgroundColor="Background Base"
            >
              <Stack gap="4">
                <Text isBold>
                  {t('readiness_attention_send_result', {
                    candidates: result.candidates_checked,
                    actionable: result.actionable_recipients,
                  })}
                </Text>
                <Text color="subtle">
                  {t('readiness_attention_delivery_result', {
                    emails: result.email_scheduled,
                    slacks: result.slack_scheduled,
                  })}
                </Text>
                <Text color="subtle">
                  {t('readiness_attention_skip_result', {
                    mainGate: result.main_gate_skips,
                    deliveryGate: result.delivery_gate_skips,
                    dormant: result.dormant_skips,
                  })}
                </Text>
              </Stack>
            </Box>
          ) : null}
        </Stack>
      </Modal.Body>
      <Modal.Footer>
        <CancelButton onClick={onClose}>{t('cancel')}</CancelButton>
        <Button
          variant="primary"
          disabled={!canSend}
          isLoading={mutation.isPending}
          onClick={() => mutation.mutate({audience, sendEmail, sendSlack})}
        >
          {t('send')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
